'use client';

import { FC, useMemo, useRef } from 'react';

import Format from '../../../../utils/format/format';
import { Badge } from '../../../badge/components/Badge/Badge';
import { Box } from '../../../box/components/Box/Box';
import { Checkbox } from '../../../checkbox/components/Checkbox/Checkbox';
import { Image } from '../../../image/components/Image/Image';
import { Link } from '../../../link/components/Link/Link';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   href: string;
   target?: '_blank' | '_self';
   noFollow?: boolean;
   image: string;
   name: string;
   producer: string;
   polymerType: string;
   technologies: string[];
   features?: string[];
   fillers?: string[];
   badgLeft?: React.ReactNode;
   badgeRight?: React.ReactNode;
   compareLabel?: string;
   toggleState?: boolean;
   fillHeight?: boolean;
   compareDisabled?: boolean;
   deferImage?: boolean;
   attributes?: Record<string, string>;
   onToggleCompare?: (checked: boolean) => void;
}

export const ProductCard: FC<Props> = ({ href, target = '_self', noFollow, image, name, producer, polymerType, technologies, features, fillers, badgLeft, badgeRight, compareLabel, toggleState, fillHeight, compareDisabled, deferImage, attributes, onToggleCompare }) => {
   const visibleFeatures = useMemo(() => (features || []).slice(0, 3), [features]);
   const remainingFeatures = (features || []).length - visibleFeatures.length;

   const checkboxIdRef = useRef(Format('product-card-compare', name));

   return (
      <Box direction="column" gap="none" borderRadius="largest" overflowY="hidden" height={fillHeight ? '100%' : 'auto'} shadow attributes={attributes}>
         <Link href={href} target={target} noFollow={noFollow} underline="none" tabIndex={-1}>
            <Box direction="column" justify="between" minHeight="150px" spacing="md" position="relative">
               <Box position="absolute" top="0" left="0" zIndex="0" height="100%" width="100%" overflow="hidden" backgroundColor="scrimmed-primary">
                  <Image src={image} alt={name} title={name} overlay="gradient-fade-out-black" width="100%" defer={deferImage} />
               </Box>

               <Box justify="between" position="relative" zIndex="1">
                  {badgLeft}
                  {badgeRight}
               </Box>
               <Box direction="column" gap="none" position="relative" zIndex="1">
                  <Typography variant="body-highlight" color="inverse-strong">
                     {name}
                  </Typography>
                  <Typography variant="body" color="inverse-strong">
                     {producer}
                  </Typography>
               </Box>
            </Box>
         </Link>
         <Box direction="column" justify="between" spacing="md" height={fillHeight ? '100%' : 'auto'} background="#fff">
            <Link href={href} target={target} noFollow={noFollow} underline="none" style={{ height: '100%' }}>
               <Box direction="column" gap="md">
                  <Typography variant="body">
                     {polymerType} {fillers?.length ? `| ${fillers.join(', ')}` : ''}
                  </Typography>
                  {technologies?.length > 0 && <Typography variant="body">{technologies.join(', ')}</Typography>}
                  {visibleFeatures?.length > 0 && (
                     <Box gap="xs" wrap="wrap">
                        {visibleFeatures.map((feature) => (
                           <Badge key={feature} size="regular" variant="neutral" weight="regular">
                              {feature}
                           </Badge>
                        ))}

                        {remainingFeatures > 0 && (
                           <Badge size="regular" variant="neutral" weight="regular">
                              +{remainingFeatures}
                           </Badge>
                        )}
                     </Box>
                  )}
               </Box>
            </Link>

            {onToggleCompare && (
               <Checkbox id={checkboxIdRef.current} gutter={false} checked={toggleState} disabled={compareDisabled} onChange={onToggleCompare}>
                  {compareLabel && <Typography variant="body">{compareLabel}</Typography>}
               </Checkbox>
            )}
         </Box>
      </Box>
   );
};
